import type { ReactNode } from 'react';
import { IconCircleCheck, IconUserPlus } from '@tabler/icons-react';
import { Skeleton } from '../../ui/Skeleton';

/** A boundary belongs to the sequence before its editable steps have loaded. */
function BoundaryCard({ icon, label }: { icon: ReactNode; label: string }) {
  return <div data-testid="automation-boundary-card" className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-transparent px-3 py-3 text-sm text-muted-foreground">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground">{icon}</span>
    <span>{label}</span>
  </div>;
}

/** The actual step surface owns its loading state as well as expanded content. */
export function AutomationBlockView({ loading = false, expanded = false, header, children }: {
  loading?: boolean; expanded?: boolean; header?: ReactNode; children?: ReactNode;
}) {
  return <div data-testid="automation-block-surface" aria-busy={loading || undefined}
    className={`rounded-lg transition-colors duration-200 hover:bg-surface-hover/40 group-focus-within:bg-surface/40 motion-reduce:transition-none ${expanded ? 'bg-surface/40' : ''}`}>
    <div className="flex items-center gap-3 px-3 py-2.5">
      {loading ? <div className="flex min-h-11 min-w-0 flex-1 items-center gap-3 text-sm font-medium">
        <span className="h-7 w-7 shrink-0"><Skeleton height="100%" /></span>
        <Skeleton containerClassName="min-w-0 flex-1" />
      </div> : header}
    </div>
    {children}
  </div>;
}

/** Keep trigger, editable steps and completion boundary in one shared layout. */
export function AutomationSequenceView({ loading = false, children }: { loading?: boolean; children?: ReactNode }) {
  return <div className="relative flex flex-col gap-5">
    <BoundaryCard icon={<IconUserPlus className="h-4 w-4" />} label="When a participant is added" />
    <div className="flex flex-col gap-5">
      {loading ? [0, 1, 2].map(index => <AutomationBlockView key={index} loading />) : children}
    </div>
    <BoundaryCard icon={<IconCircleCheck className="h-4 w-4" />} label="Stops when the conversation completes" />
  </div>;
}

/** The campaign automation page stays in place while its data or module loads. */
export function AutomationSurfaceView({ loading = false, loadingLabel = 'Loading automation', toolbar, controls, sequence, footer, children }: {
  loading?: boolean; loadingLabel?: string; toolbar?: ReactNode; controls?: ReactNode;
  sequence?: ReactNode; footer?: ReactNode; children?: ReactNode;
}) {
  return <div role={loading ? 'status' : undefined} aria-label={loading ? loadingLabel : undefined} aria-busy={loading || undefined}
    className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-10">
    <div className="flex items-center justify-between gap-3">{toolbar}</div>
    <div className="flex items-start justify-between gap-4">
      <h1 className="text-3xl font-bold tracking-tight text-foreground">Automation</h1>
      <div className="min-w-[9rem]">{loading ? <Skeleton height="2rem" /> : controls}</div>
    </div>
    <div>{loading ? <AutomationSequenceView loading /> : sequence}</div>
    <div className="flex justify-end border-t border-border pt-6">
      {loading ? <button type="button" disabled className="flex items-center gap-2 rounded-lg bg-surface-raised px-4 py-2 text-sm font-bold text-muted-foreground">
        <IconUserPlus className="h-4 w-4" />Invite people
      </button> : footer}
    </div>
    {children}
  </div>;
}
